import React, { useEffect } from 'react';
import {
  Box,
  CssBaseline,
  ThemeProvider,
  createTheme,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Badge,
  Menu,
  MenuItem,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  Store,
  RestaurantMenu,
  ShoppingCart,
  Notifications,
  Brightness4,
  Brightness7,
  AccountCircle,
  People as PeopleIcon,
  Security as SecurityIcon,
  Logout as LogoutIcon,
  TrendingUp,
  Description as DescriptionIcon,
} from '@mui/icons-material';

import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import {
  toggleSidebar,
  setTheme,
  hideSnackbar,
  markAllNotificationsAsRead,
} from '../store/slices/uiSlice';
import { logout, selectUser, selectIsAuthenticated, selectHasPermission } from '../store/slices/authSlice';
import { signalRService } from '../services/signalRService'; // Re-enabled with disabled stub
import { APP_CONFIG } from '../config/app';
import VersionDisplay from '../components/Common/VersionDisplay';

const drawerWidth = 240;

const MainLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  
  const { 
    theme, 
    sidebarOpen, 
    notifications, 
    snackbar 
  } = useAppSelector((state) => state.ui);

  const user = useAppSelector(selectUser);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const canManageUsers = useAppSelector(state => selectHasPermission(state, 'users:manage'));
  const canManageRoles = useAppSelector(state => selectHasPermission(state, 'roles:manage'));
  const canViewRevenue = useAppSelector(state => selectHasPermission(state, 'revenue:read'));
  const canViewReports = useAppSelector(state => selectHasPermission(state, 'reports:read'));
  const canViewOrders = useAppSelector(state => selectHasPermission(state, 'orders:read'));
  // New: control visibility for dashboard/branches/mappings
  const canViewBranches = useAppSelector(state => selectHasPermission(state, 'branches:read'));
  const canViewMappings = useAppSelector(state => selectHasPermission(state, 'mappings:read'));
  const canViewDashboard = useAppSelector(state =>
    selectHasPermission(state, 'orders:read')
  );

  const [notificationAnchorEl, setNotificationAnchorEl] = React.useState<null | HTMLElement>(null);
  const [userMenuAnchorEl, setUserMenuAnchorEl] = React.useState<null | HTMLElement>(null);

  // Define menu item type
  interface MenuItem {
    text: string;
    icon: React.ReactNode;
    path: string;
    title: string;
  }

  // Standard menu items
  const menuItems: MenuItem[] = [
    ...(canViewDashboard ? [{ text: 'Dashboard', icon: <Dashboard />, path: '/', title: 'Dashboard' }] : []),
    ...(canViewRevenue ? [{ text: 'Revenue', icon: <TrendingUp />, path: '/revenue', title: 'Revenue Dashboard' }] : []),
    ...(canViewBranches ? [{ text: 'Branches', icon: <Store />, path: '/branches', title: 'Branch Management' }] : []),
    ...(canViewMappings ? [{ text: 'Menu Mappings', icon: <RestaurantMenu />, path: '/mappings', title: 'Menu Mappings' }] : []),
    ...(canViewOrders ? [{ text: 'Orders', icon: <ShoppingCart />, path: '/orders', title: 'Orders Management' }] : []),
    ...(canManageUsers ? [{ text: 'Users', icon: <PeopleIcon />, path: '/users', title: 'User Management' }] : []),
    ...(canManageRoles ? [{ text: 'Roles', icon: <SecurityIcon />, path: '/roles', title: 'Role Management' }] : []),
  ];

  const muiTheme = createTheme({
    palette: {
      mode: theme,
      primary: {
        main: '#1976d2',
      },
      secondary: {
        main: '#dc004e',
      },
    },
  });

  useEffect(() => {
    // SignalR disabled for now - using stub implementation
    signalRService.connect().catch(console.error);
    
    return () => {
      signalRService.disconnect();
    };
  }, []);

  const handleDrawerToggle = () => {
    dispatch(toggleSidebar());
  };

  const handleThemeToggle = () => {
    dispatch(setTheme(theme === 'light' ? 'dark' : 'light'));
  };

  const handleNotificationClick = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchorEl(event.currentTarget);
  };
  const handleNotificationClose = () => {
    setNotificationAnchorEl(null);
    dispatch(markAllNotificationsAsRead());
  };

  const handleUserMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchorEl(null);
  };

  const handleLogout = async () => {
    setUserMenuAnchorEl(null);
    try {
      await dispatch(logout()).unwrap();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleSnackbarClose = () => {
    dispatch(hideSnackbar());
  };

  const unreadCount = notifications.filter(n => !n.read).length;
  const getCurrentPageTitle = () => {
    // Handle Reports route specifically
    if (location.pathname === '/reports') {
      return 'Reports';
    }
    
    const currentItem = menuItems.find(item => item.path === location.pathname);
    return currentItem?.title || APP_CONFIG.DEFAULT_PAGE_TITLE;
  };

  const drawer = (
    <Box>
      <Toolbar>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {APP_CONFIG.USE_ICON_IN_SIDEBAR && <RestaurantMenu color="primary" />}
          <Typography variant="h6" noWrap component="div">
            {APP_CONFIG.SIDEBAR_TITLE}
          </Typography>
        </Box>
      </Toolbar>
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => navigate(item.path)}
            >
              <ListItemIcon>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
        
        {/* Reports Menu - only shown if user has reports:read permission */}
        {canViewReports && (
          <ListItem disablePadding>
            <ListItemButton
              selected={location.pathname === '/reports'}
              onClick={() => navigate('/reports')}
            >
              <ListItemIcon>
                <DescriptionIcon />
              </ListItemIcon>
              <ListItemText primary="Reports" />
            </ListItemButton>
          </ListItem>
        )}
      </List>
    </Box>
  );

  return (
    <ThemeProvider theme={muiTheme}>
      <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
        <CssBaseline />
        
        {/* App Bar */}
        <AppBar
          position="fixed"
          sx={{
            width: { sm: `calc(100% - ${sidebarOpen ? drawerWidth : 0}px)` },
            ml: { sm: sidebarOpen ? `${drawerWidth}px` : 0 },
            transition: (theme) => theme.transitions.create(['width', 'margin'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
          }}
        >
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
              {getCurrentPageTitle()}
            </Typography>
            {/* Theme Toggle */}
            <IconButton color="inherit" onClick={handleThemeToggle}>
              {theme === 'dark' ? <Brightness7 /> : <Brightness4 />}
            </IconButton>

            {/* Version Display */}
            <VersionDisplay />

            {/* Notifications */}
            <IconButton
              color="inherit"
              onClick={handleNotificationClick}
            >
              <Badge badgeContent={unreadCount} color="error">
                <Notifications />
              </Badge>
            </IconButton>

            {/* User Menu */}
            {isAuthenticated && user && (
              <IconButton color="inherit" onClick={handleUserMenuClick}>
                <AccountCircle />
              </IconButton>
            )}
          </Toolbar>
        </AppBar>

        {/* Notifications Menu */}
        <Menu
          anchorEl={notificationAnchorEl}
          open={Boolean(notificationAnchorEl)}
          onClose={handleNotificationClose}
          PaperProps={{
            sx: { width: 320, maxHeight: 400 }
          }}
        >
          {notifications.length === 0 ? (
            <MenuItem>
              <Typography variant="body2" color="text.secondary">
                No notifications
              </Typography>
            </MenuItem>
          ) : (
            notifications.slice(0, 5).map((notification) => (
              <MenuItem key={notification.id} onClick={handleNotificationClose}>
                <Box>
                  <Typography variant="subtitle2" color={notification.read ? 'text.secondary' : 'text.primary'}>
                    {notification.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {notification.message}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(notification.timestamp).toLocaleString()}
                  </Typography>
                </Box>
              </MenuItem>
            ))
          )}
          {notifications.length > 5 && (
            <MenuItem onClick={() => navigate('/notifications')}>
              <Typography variant="body2" color="primary">
                View all notifications
              </Typography>
            </MenuItem>
          )}
        </Menu>

        {/* User Menu */}
        <Menu
          anchorEl={userMenuAnchorEl}
          open={Boolean(userMenuAnchorEl)}
          onClose={handleUserMenuClose}
          PaperProps={{
            sx: { width: 250 }
          }}
        >
          {user && (
            <>
              <MenuItem disabled>
                <Box>
                  <Typography variant="subtitle1">{user.username}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {user.email}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Role: {user.roles?.map(r => r.name).join(', ') || 'No role assigned'}
                  </Typography>
                </Box>
              </MenuItem>
              <Divider />
              <MenuItem onClick={() => { handleUserMenuClose(); navigate('/profile'); }}>
                <ListItemIcon>
                  <AccountCircle fontSize="small" />
                </ListItemIcon>
                <ListItemText>Profile</ListItemText>
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Logout</ListItemText>
              </MenuItem>
            </>
          )}
        </Menu>

        {/* Sidebar */}
        <Box
          component="nav"
          sx={{ 
            width: { sm: sidebarOpen ? drawerWidth : 0 }, 
            flexShrink: { sm: 0 },
            height: '100vh'
          }}
        >
          <Drawer
            variant="temporary"
            open={sidebarOpen}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true, // Better open performance on mobile.
            }}
            sx={{
              display: { xs: 'block', sm: 'none' },
              '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
            }}
          >
            {drawer}
          </Drawer>
          
          <Drawer
            variant="persistent"
            sx={{
              display: { xs: 'none', sm: 'block' },
              '& .MuiDrawer-paper': { 
                boxSizing: 'border-box', 
                width: drawerWidth,
                transition: (theme) => theme.transitions.create('width', {
                  easing: theme.transitions.easing.sharp,
                  duration: theme.transitions.duration.enteringScreen,
                }),
              },
            }}
            open={sidebarOpen}
          >
            {drawer}
          </Drawer>
        </Box>

        {/* Main Content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 3,
            width: { sm: `calc(100% - ${sidebarOpen ? drawerWidth : 0}px)` },
            mt: 8, // Account for AppBar height
            transition: (theme) => theme.transitions.create(['width', 'margin'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
            overflowY: 'auto', // Enable vertical scrolling
            height: (theme) => `calc(100vh - ${theme.mixins.toolbar.minHeight}px)`, // Dynamically calculate based on toolbar height
            maxHeight: '100vh',
            position: 'relative',
          }}
        >
          <Outlet />
        </Box>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbar.severity}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </ThemeProvider>
  );
};

export default MainLayout;
